import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, MapPin, Truck as TruckIcon, Clock, Phone, User, Radio } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { trackingApi } from '../lib/api';

// India major city coordinates (approx)
export const cityCoords = {
  'Mumbai': [19.0760, 72.8777], 'Delhi': [28.7041, 77.1025], 'Bengaluru': [12.9716, 77.5946],
  'Chennai': [13.0827, 80.2707], 'Kolkata': [22.5726, 88.3639], 'Hyderabad': [17.3850, 78.4867],
  'Ahmedabad': [23.0225, 72.5714], 'Pune': [18.5204, 73.8567], 'Surat': [21.1702, 72.8311],
  'Jaipur': [26.9124, 75.7873], 'Lucknow': [26.8467, 80.9462], 'Kanpur': [26.4499, 80.3319],
  'Nagpur': [21.1458, 79.0882], 'Indore': [22.7196, 75.8577], 'Bhopal': [23.2599, 77.4126],
  'Coimbatore': [11.0168, 76.9558], 'Vadodara': [22.3072, 73.1812], 'Ludhiana': [30.9010, 75.8573],
  'Agra': [27.1767, 78.0081], 'Nashik': [19.9975, 73.7898], 'Faridabad': [28.4089, 77.3178],
  'Meerut': [28.9845, 77.7064], 'Rajkot': [22.3039, 70.8022], 'Kalyan': [19.2437, 73.1355],
  'Vasai': [19.3919, 72.8397], 'Varanasi': [25.3176, 82.9739], 'Srinagar': [34.0837, 74.7973],
  'Aurangabad': [19.8762, 75.3433], 'Dhanbad': [23.7957, 86.4304], 'Amritsar': [31.6340, 74.8723],
  'Navi Mumbai': [19.0330, 73.0297], 'Allahabad': [25.4358, 81.8463], 'Ranchi': [23.3441, 85.3096],
  'Howrah': [22.5958, 88.2636], 'Jabalpur': [23.1815, 79.9864], 'Gwalior': [26.2183, 78.1828],
  'Vijayawada': [16.5062, 80.6480], 'Jodhpur': [26.2389, 73.0243], 'Madurai': [9.9252, 78.1198],
  'Raipur': [21.2514, 81.6296], 'Kota': [25.2138, 75.8648], 'Guwahati': [26.1445, 91.7362],
  'Chandigarh': [30.7333, 76.7794], 'Thiruvananthapuram': [8.5241, 76.9366], 'Kochi': [9.9312, 76.2673],
  'Bhubaneswar': [20.2961, 85.8245], 'Mysore': [12.2958, 76.6394], 'Tiruchirappalli': [10.7905, 78.7047],
  'Hubli': [15.3647, 75.1240], 'Mangalore': [12.9141, 74.8560]
};

const getCoords = (city) => {
  const key = city.split(',')[0].trim();
  return cityCoords[key] || [20.5937, 78.9629]; // India center fallback
};

const createIcon = (color, letter) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background:${color};color:white;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 3px 10px rgba(0,0,0,.25);border:2px solid white;"><span style="transform:rotate(45deg);font-weight:bold;font-size:12px;">${letter}</span></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const truckIcon = L.divIcon({
  className: 'truck-marker',
  html: `<div style="background:#00A651;color:white;width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(0,166,81,.5);border:3px solid white;animation:pulse 2s infinite;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11"/><path d="M14 9h4l4 4v5c0 .6-.4 1-1 1h-2"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      map.fitBounds(points, { padding: [60, 60] });
    }
  }, [points, map]);
  return null;
}

export default function TrackingMap({ open, onClose, load }) {
  const [progress, setProgress] = useState(load?.progress || 0);
  const [liveGps, setLiveGps] = useState(null); // { lat, lng, updated_at }

  useEffect(() => {
    if (!open || !load) return;
    setProgress(load?.progress || 65);
    let iv;
    const fetchLive = () => {
      trackingApi.get(load.id).then(r => {
        if (r && r.lat != null && r.lng != null) setLiveGps(r);
      }).catch(() => {});
    };
    fetchLive();
    iv = setInterval(fetchLive, 8000);
    return () => clearInterval(iv);
  }, [open, load]);

  if (!open || !load) return null;

  const origin = getCoords(load.origin);
  const destination = getCoords(load.destination);
  // Use live GPS if available; else interpolate along the straight line
  const truckPos = liveGps ? [liveGps.lat, liveGps.lng] : [
    origin[0] + (destination[0] - origin[0]) * (progress / 100),
    origin[1] + (destination[1] - origin[1]) * (progress / 100),
  ];
  const isLive = !!liveGps;
  const lastUpdate = liveGps ? new Date(liveGps.updated_at).toLocaleTimeString('en-IN') : null;

  const totalDist = Math.round(Math.sqrt(Math.pow((destination[0] - origin[0]) * 111, 2) + Math.pow((destination[1] - origin[1]) * 100, 2)));
  const remaining = Math.round(totalDist * (1 - progress / 100));
  const etaHours = Math.round(remaining / 45);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[#0B2545]">Live Tracking</h3>
              {isLive ? (
                <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[10px]"><Radio className="w-3 h-3 mr-1 animate-pulse" />REAL GPS</Badge>
              ) : (
                <Badge className="bg-red-50 text-red-600 border-0 text-[10px]"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1 animate-pulse"></span>SIMULATED</Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 font-mono">{load.id} • {load.vehicleNumber}{lastUpdate ? ` • Updated ${lastUpdate}` : ''}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid md:grid-cols-3">
          <div className="md:col-span-2 h-[380px] relative">
            <MapContainer center={[20.5937, 78.9629]} zoom={5} className="w-full h-full" scrollWheelZoom={false} style={{ zIndex: 1 }}>
              <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={origin} icon={createIcon('#0B2545', 'A')}>
                <Popup>Origin: {load.origin}</Popup>
              </Marker>
              <Marker position={destination} icon={createIcon('#B37800', 'B')}>
                <Popup>Destination: {load.destination}</Popup>
              </Marker>
              <Marker position={truckPos} icon={truckIcon}>
                <Popup>Vehicle {load.vehicleNumber} — {progress}% complete</Popup>
              </Marker>
              <Polyline positions={[origin, truckPos]} pathOptions={{ color: '#00A651', weight: 4, opacity: 0.9 }} />
              <Polyline positions={[truckPos, destination]} pathOptions={{ color: '#B37800', weight: 3, opacity: 0.5, dashArray: '8, 8' }} />
              <FitBounds points={[origin, destination]} />
            </MapContainer>
          </div>

          <div className="p-5 space-y-4 bg-[#F7FAF9]">
            <div>
              <p className="text-xs text-gray-500 uppercase">Route</p>
              <div className="flex items-center gap-2 mt-1">
                <div>
                  <p className="font-bold text-[#0B2545]">{load.origin.split(',')[0]}</p>
                  <p className="text-[10px] text-gray-500">Origin</p>
                </div>
                <div className="flex-1 border-t-2 border-dashed border-gray-300"></div>
                <div className="text-right">
                  <p className="font-bold text-[#0B2545]">{load.destination.split(',')[0]}</p>
                  <p className="text-[10px] text-gray-500">Destination</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Progress</span><span className="font-semibold text-[#00A651]">{progress}%</span></div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full brand-gradient transition-all duration-500" style={{ width: `${progress}%` }}></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white rounded-lg p-3">
                <p className="text-[10px] text-gray-500 uppercase">Distance</p>
                <p className="font-bold text-[#0B2545]">~{totalDist} km</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-[10px] text-gray-500 uppercase">Remaining</p>
                <p className="font-bold text-[#00A651]">~{remaining} km</p>
              </div>
              <div className="bg-white rounded-lg p-3 col-span-2">
                <p className="text-[10px] text-gray-500 uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> ETA</p>
                <p className="font-bold text-[#0B2545]">~{etaHours} hours</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase mb-1">Driver</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center text-white text-xs font-bold">{(load.driver || 'DR').split(' ').map(n => n[0]).join('')}</div>
                  <div>
                    <p className="text-sm font-semibold text-[#0B2545]">{load.driver}</p>
                    <p className="text-[10px] text-gray-500 font-mono">{load.driverPhone}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-8"><Phone className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { transform: scale(1); box-shadow: 0 4px 14px rgba(0,166,81,.5); } 50% { transform: scale(1.1); box-shadow: 0 6px 20px rgba(0,166,81,.8); } }
        .leaflet-container { font-family: 'Inter', sans-serif; }
      `}</style>
    </div>
  );
}
